import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { Shield, Award, MapPin, Users, FileCheck, Truck, Phone, ArrowRight } from "lucide-react";
import ueberUns from "@/assets/schober/ueber-uns.webp";
import ctaBg from "@/assets/schober/cta-ueber-uns.webp";
import { Testimonials } from "@/components/site/Testimonials";

export const Route = createFileRoute("/ueber-uns")({
  head: () => ({
    meta: [
      { title: "Über uns – Patric Schober & Team | Schober Transportservice Wien" },
      {
        name: "description",
        content:
          "Patric Schober – Polizist & Gründer von Schober Transportservice. Seit 2023 stehen wir mit Angebot, Diskretion und eigenem Team für Umzug, Entrümpelung & Räumung in Wien, NÖ und Burgenland.",
      },
      { property: "og:title", content: "Über uns – Patric Schober & Team" },
      {
        property: "og:description",
        content:
          "Wer wir sind, woran wir glauben, wie wir arbeiten – und warum Diskretion und Verlässlichkeit unser Markenkern sind.",
      },
      { property: "og:url", content: "https://schober-transportservice.at/ueber-uns" },
    ],
    links: [{ rel: "canonical", href: "https://schober-transportservice.at/ueber-uns" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Schober Transportservice",
          description:
            "Transportunternehmen mit Gewerbeberechtigung Entrümpler. Wir räumen Wohnungen, Häuser und Gewerbeobjekte, transportieren das Räumgut ab und führen Sperrgut befugten Entsorgungsbetrieben zur ordnungsgemäßen Entsorgung zu. Eigene abfallwirtschaftliche Leistungen im Sinne des AWG 2002 erbringen wir nicht.",
          founder: { "@type": "Person", name: "Patric Schober" },
          foundingDate: "2023-10",
          telephone: "+43 680 230 87 00",
          areaServed: ["Wien", "Niederösterreich", "Burgenland", "Österreich"],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "16",
          },
        }),
      },
    ],
  }),
  component: UeberUnsPage,
});

const werte = [
  {
    icon: Shield,
    title: "Diskretion",
    text: "Was in Ihren vier Wänden passiert, bleibt zwischen uns. Auf Wunsch unbeschrifteter Transporter, neutrale Arbeitskleidung, keine Kommunikation mit Nachbarn.",
  },
  {
    icon: FileCheck,
    title: "Preistransparenz",
    text: "Was wir vorab kalkuliert haben, gilt. Keine Nachverrechnung, keine versteckten Kosten, kein Stundenlohn-Spiel.",
  },
  {
    icon: Users,
    title: "Eigenes Team",
    text: "Alle Aufträge werden von festangestelltem, geschultem Personal durchgeführt – nicht von wechselnden Subunternehmern.",
  },
  {
    icon: Award,
    title: "Verantwortung",
    text: "Wir transportieren das Räumgut ab, sichern persönliche Dokumente und übergeben besenrein. Punkt.",
  },
];

const kompetenzen = [
  "Privatumzüge & Übersiedlungen (Wien, NÖ, EU)",
  "Firmen- und Büroumzüge mit Wochenend-Abwicklung",
  "Wohnungs- und Haushaltsauflösungen im Trauerfall",
  "Entrümpelungen von Keller bis Einfamilienhaus",
  "Diskrete Räumung von Messie-Wohnungen mit Vollschutz",
  "Demontage von Küchen, Bädern, Bodenbelägen",
  "Kleintransporte, IKEA-Abholungen, Klaviertransporte",
  "Grünschnitt-Abtransport und Gartenpflege",
];

const einsatzgebiete = [
  { name: "Wien", details: "Alle 23 Gemeindebezirke" },
  { name: "Niederösterreich", details: "Schwerpunkt: Wr. Neustadt, Mödling, Baden, Neunkirchen, Bruck/Leitha" },
  { name: "Burgenland", details: "Bezirke Eisenstadt und Mattersburg" },
  { name: "Österreich", details: "Auf Anfrage österreichweit" },
  { name: "EU-Ausland", details: "Auslandsumzüge nach DE, IT, SK, SI, HU, CZ" },
];

const zertifikate = [
  { title: "Befugte Partnerbetriebe", text: "Weitere Behandlung des Räumguts ausschließlich über befugte Betriebe in Wien & NÖ." },
  { title: "Versichert bis 25.000 €", text: "Transport- und Haftpflichtversicherung pro Auftrag." },
  { title: "DSGVO-konform", text: "Aktentransport in versiegelten Boxen, zertifizierte Vernichtungspartner." },
  { title: "Eingetragenes Unternehmen", text: "Patric Schober Transportservice e.U., UID: ATU…, Gewerbeschein gültig." },
];

function UeberUnsPage() {
  return (
    <>
      <PageHero title="ÜBER UNS" subtitle="Ihr kompetenter Partner" />

      {/* Patric Schober Story */}
      <section className="container mx-auto max-w-7xl grid gap-12 px-4 py-20 lg:grid-cols-[1.3fr_1fr] lg:items-start">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange-on-dark">
            Die Geschichte hinter Schober Transportservice
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-brand-navy md:text-5xl">
            Patric Schober – Polizist, Unternehmer, Familienmensch
          </h1>
          <div className="mt-8 space-y-5 leading-relaxed text-foreground/85">
            <p>
              Nach meiner Lehre als Buchhalter arbeitete ich einige Jahre lang nicht nur im Büro,
              sondern auch im Lager einer großen Firma. Bei dieser Tätigkeit, die zu meinen Stärken
              zählt, konnte ich umfassende Erfahrung in den Bereichen Logistik und Auftragsabwicklung
              sammeln, die mir bei Schober Transportservice heute jeden Tag zugutekommen. Nach ein
              paar Jahren im Büro beschloss ich, die Polizeischule in Wien zu absolvieren, und seit
              September 2013 bin ich in Wien Favoriten als Polizist tätig.
            </p>
            <p>
              Im Zuge meiner Tätigkeit als Polizist habe ich im Laufe der Jahre die unterschiedlichsten
              Wohnungen und Häuser gesehen – darunter viele, in denen Bewohner oder Angehörige beim
              Thema Transport, Entrümpelung oder der Räumung stark verwahrloster Objekte vor großen
              Herausforderungen standen. Oft wussten Menschen nicht, an wen sie sich wenden sollen.
              Das hat in mir den Wunsch geweckt, genau diesen Menschen als kompetenter, verlässlicher
              Partner zur Seite zu stehen – ohne Vorurteile, ohne Geschwätz, ohne Druck.
            </p>
            <p>
              So gründete ich im Oktober 2023 das Einzelunternehmen Patric Schober Transportservice.
              Heute führe ich es gemeinsam mit meiner Familie und meinen Geschäftspartnern – mit einem
              eigenen geschulten Team und eigenem Fuhrpark. Wir arbeiten ohne Subunternehmer. Sie haben
              vom ersten Anruf bis zur Schlüsselübergabe einen einzigen Ansprechpartner.
            </p>
            <p>
              Durch meine langjährige Erfahrung als Polizist weiß ich, wie wichtig Vertrauen und
              Sicherheit sind. Diese Werte stehen im Mittelpunkt unseres Handelns – besonders, wenn
              es um Ihr Eigentum, Ihre vier Wände oder das Erbe eines Angehörigen geht.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="tel:00436802308700"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground transition hover:scale-105"
            >
              <Phone className="h-4 w-4" /> +43 680 230 87 00
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-navy transition hover:bg-brand-navy hover:text-white"
            >
              Schreiben Sie uns <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="overflow-hidden rounded-md bg-muted shadow-xl lg:sticky lg:top-24">
          <img
            src={ueberUns}
            alt="Patric Schober, Gründer von Schober Transportservice"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      {/* Werte */}
      <section className="bg-brand-navy py-16 text-white md:py-20">
        <div className="container mx-auto max-w-7xl px-4">
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Woran wir glauben
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold md:text-4xl">
            Vier Werte. Kein Marketing-Geschwätz.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {werte.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-lg bg-white/5 p-6 ring-1 ring-white/10"
              >
                <Icon className="h-8 w-8 text-brand-orange-on-dark" />
                <h3 className="mt-4 font-display text-lg font-bold">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/85">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kompetenzen + Einsatzgebiet (2-Spalter) */}
      <section className="container mx-auto max-w-7xl grid gap-12 px-4 py-20 lg:grid-cols-2">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Was wir können
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy md:text-4xl">
            Unsere Kompetenzen
          </h2>
          <p className="mt-4 leading-relaxed text-foreground/85">
            Von der kleinen IKEA-Abholung bis zur kompletten Werkstattübersiedlung an einem
            Wochenende – wir decken die gesamte Bandbreite ab.
          </p>
          <ul className="mt-6 grid gap-2">
            {kompetenzen.map((k) => (
              <li key={k} className="flex gap-3 border-b border-border py-3 text-sm">
                <Truck className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
                <span>{k}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/leistungen"
            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-orange hover:underline"
          >
            Alle Leistungen im Detail <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Wo wir tätig sind
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy md:text-4xl">
            Einsatzgebiet
          </h2>
          <p className="mt-4 leading-relaxed text-foreground/85">
            Schwerpunkt Wien und Wiener Umland. Bei größeren Aufträgen oder Auslandsumzügen
            kommen wir auch österreichweit oder ins EU-Ausland.
          </p>
          <ul className="mt-6 grid gap-3">
            {einsatzgebiete.map((g) => (
              <li
                key={g.name}
                className="flex gap-3 rounded-lg border border-border bg-muted/40 p-4"
              >
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                <div>
                  <p className="font-semibold text-brand-navy">{g.name}</p>
                  <p className="text-sm text-muted-foreground">{g.details}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Zertifikate / Vertrauen */}
      <section className="bg-muted py-16 md:py-20">
        <div className="container mx-auto max-w-7xl px-4">
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Vertrauen schwarz auf weiß
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy md:text-4xl">
            Zertifikate, Versicherung & Compliance
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {zertifikate.map((z) => (
              <div
                key={z.title}
                className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-border"
              >
                <Shield className="h-7 w-7 text-brand-orange" />
                <h3 className="mt-3 font-display text-lg font-bold text-brand-navy">
                  {z.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{z.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative bg-cover bg-center py-25 md:py-40"
        style={{ backgroundImage: `url(${ctaBg})` }}
      >
        <div className="absolute inset-0 bg-black/40" aria-hidden />
        <div className="relative z-10 container mx-auto flex flex-col items-center gap-6 px-4 text-center text-brand-orange-foreground">
          <h2 className="font-display text-3xl font-extrabold md:text-4xl text-brand-orange-on-dark">
            Sie benötigen unsere Hilfe?
          </h2>
          <p className="max-w-2xl">
            Egal ob Transport, Demontage, Umzug oder Entrümpelung – wir sind Ihr
            zuverlässiger Partner.
          </p>
          <Link
            to="/kontakt"
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-brand-orange px-10 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground shadow-lg transition-all hover:scale-105"
          >
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
      <Testimonials />
    </>
  );
}
